import React, { useCallback, useEffect, useState } from 'react';

import { FormControlLabel, FormHelperText, Radio, RadioGroup } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { MultiValue, SingleValue } from 'react-select';
import debounce from 'debounce-promise';

import { LoadingType, setLoadingState } from 'prequalificationV2/actions';
import { setLoanPortal } from 'prequalificationV2/actions/dashboardPortal';
import Loader from 'components/Loader';
import { useAppSelector } from 'prequalificationV2/hooks/useAppSelector';
import Section from 'prequalificationV2/components/LoanPortal/Sections/Section';
import { DashboardTabName } from 'enums/DashboardTabName';
import styles from 'prequalificationV2/pages/Dashboard/steps/InformationTabs/InformationTabs.module.scss';
import * as notificationManager from 'notifications/index';
import { Button } from 'components/Buttons/Button';
import { FailureNotificationType } from 'notifications/index';
import { useAppDispatch } from 'prequalificationV2/hooks/useAppDispatch';
import { convertStringToBoolean } from 'prequalificationV2/utils/stringUtils';
import Input from 'components/Inputs/Input';
import PhoneNumberInput from 'components/Inputs/PhoneNumberInput';
import {
  contractorInformationApi,
  IntermediarySuggestionDto,
  ContractorInformationResponseDto,
} from 'prequalificationV2/api/SmallLoanProgram/ContractorInformationApi';
import { isValidEmail, validatePhoneNumber } from 'prequalificationV2/utils/validationUtils';
import states from 'enums/StatesList';
import { formatOptionLabel, getInputSelectOptions, OptionType } from 'prequalificationV2/utils/inputSelect';
import Select from 'components/Select';
import SelectCreatable from 'components/SelectCreatable';
import { getApplicationInfoForDashboardThunk } from 'prequalificationV2/thunks/getApplicationInfoForDashboard';
import { DEFAULT_DEBOUNCE_TIMEOUT } from 'prequalificationV2/utils/constants';

import { ContractorContactOption, ContractorInformationFormValues } from './contractorInformationTab.types';
import {
  ContractorInfoField,
  ContractorInfoLabel,
  ContractorLocatedRadioValue,
} from './contractorInformationTab.enums';

const MIN_SEARCH_LENGTH = 4;

const stateOptions = states.map(({ value }) => ({ value: value, label: value }));

const ContractorInformationTab = () => {
  const dispatch = useAppDispatch();

  const loading = useAppSelector(
    (state) => state.loading[`${LoadingType.RequiredInformationUpdating}${DashboardTabName.ContractorInfo}`],
  );
  const isContractorInformationLoading = useAppSelector(
    (state) => state.loading[`${LoadingType.GetData}${DashboardTabName.ContractorInfo}`],
  );
  const { applicationId, intermediaryId: applicationIntermediaryId } = useAppSelector((state) => state.application);
  const { borrowerId } = useAppSelector((state) => state.borrower);
  const { contractorInformationComplete } = useAppSelector((state) => state.dashboardPortal);

  const [isContractorInfoOpened, setIsContractorInfoOpened] = useState<boolean>(false);
  const [foundSuggestions, setFoundSuggestions] = useState<IntermediarySuggestionDto[]>([]);
  const [isManualEntry, setIsManualEntry] = useState<boolean>(false);
  const [contactOptions, setContactOptions] = useState<ContractorContactOption[]>([]);
  const [currentBusinessNameInputValue, setCurrentBusinessNameInputValue] = useState('');
  const [currentContactNameInputValue, setCurrentContactNameInputValue] = useState('');

  const { register, trigger, handleSubmit, watch, formState, setValue, clearErrors, unregister, ...methods } =
    useForm<ContractorInformationFormValues>({
      defaultValues: {
        [ContractorInfoField.ContractorLocated]: null,
        [ContractorInfoField.ContractorBusinessName]: '',
        [ContractorInfoField.IntermediaryId]: '',
        [ContractorInfoField.ContractorCity]: '',
        [ContractorInfoField.ContractorState]: '',
        [ContractorInfoField.ContractorContactName]: '',
        [ContractorInfoField.ContractorPhone]: '',
        [ContractorInfoField.ContractorContactEmail]: '',
      },
    });

  const watcher = watch();

  const setFormValuesFromDto = (contractorInformation: ContractorInformationResponseDto): void => {
    setValue(ContractorInfoField.ContractorLocated, contractorInformation.contractorIsLocated);
    setValue(ContractorInfoField.ContractorBusinessName, contractorInformation.businessName || '');
    setValue(ContractorInfoField.ContractorCity, contractorInformation.city || '');
    setValue(ContractorInfoField.ContractorState, contractorInformation.state || '');
    setValue(ContractorInfoField.ContractorPhone, contractorInformation.contactPhoneNumber || '');
    setValue(ContractorInfoField.ContractorContactName, contractorInformation.contactName || '');
    setValue(ContractorInfoField.ContractorContactEmail, contractorInformation.contactEmail || '');
  };

  useEffect(() => {
    const getContractorInformation = async () => {
      try {
        dispatch(
          setLoadingState({
            type: `${LoadingType.GetData}${DashboardTabName.ContractorInfo}`,
            loading: true,
          }),
        );

        const response = await contractorInformationApi.get(applicationId!);

        setFormValuesFromDto(response);
        setIsContractorInfoOpened(!!response.contractorIsLocated);

        if (response.contractorIsLocated) {
          setIsManualEntry(!applicationIntermediaryId);

          if (applicationIntermediaryId && response.businessName) {
            const suggestions = await contractorInformationApi.getIntermediarySuggestions(response.businessName);
            const matchedIntermediary = suggestions.find((suggestion) => suggestion.id === applicationIntermediaryId);

            if (matchedIntermediary) {
              setContactOptions(matchedIntermediary.intermediaryContacts);

              if (!response.city || !response.state) {
                setValue(ContractorInfoField.ContractorCity, matchedIntermediary.intermediaryCity || '');
                setValue(ContractorInfoField.ContractorState, matchedIntermediary.intermediaryState || '');
              }
            }
          }
        }
      } catch (err) {
        return notificationManager.notifyFailure(FailureNotificationType.UnknownError);
      } finally {
        dispatch(
          setLoadingState({
            type: `${LoadingType.GetData}${DashboardTabName.ContractorInfo}`,
            loading: false,
          }),
        );
      }
    };

    getContractorInformation();
  }, []);

  useEffect(() => {
    register(ContractorInfoField.ContractorLocated, {
      validate: (value) => value !== null,
    });
  }, []);

  useEffect(() => {
    if (isContractorInfoOpened) {
      register(ContractorInfoField.ContractorBusinessName, {
        required: 'Contractor Business Name is required',
      });
      register(ContractorInfoField.IntermediaryId);
      register(ContractorInfoField.ContractorCity);
      register(ContractorInfoField.ContractorState);
      register(ContractorInfoField.ContractorContactName);
      register(ContractorInfoField.ContractorPhone, {
        required: false,
        validate: (value) => (!value ? true : validatePhoneNumber(value)),
      });
      register(ContractorInfoField.ContractorContactEmail, {
        validate: (value) => !value || isValidEmail(value) || 'Please enter a valid email address',
      });
    } else {
      unregister([
        ContractorInfoField.ContractorBusinessName,
        ContractorInfoField.IntermediaryId,
        ContractorInfoField.ContractorCity,
        ContractorInfoField.ContractorState,
        ContractorInfoField.ContractorContactName,
        ContractorInfoField.ContractorPhone,
        ContractorInfoField.ContractorContactEmail,
      ]);
    }
  }, [isContractorInfoOpened, contractorInformationComplete]);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- contractorInformationApi is a module-scoped singleton, setFoundSuggestions is a stable state setter
  const loadContractorSuggestions = useCallback(
    debounce(async (inputValue: string) => {
      try {
        if (inputValue.length >= MIN_SEARCH_LENGTH) {
          const intermediarySuggestions = await contractorInformationApi.getIntermediarySuggestions(inputValue);

          setFoundSuggestions(intermediarySuggestions);

          return getInputSelectOptions(intermediarySuggestions.map((suggestion) => suggestion.intermediaryName));
        }

        setFoundSuggestions([]);

        return [];
      } catch {
        setFoundSuggestions([]);

        return [];
      }
    }, DEFAULT_DEBOUNCE_TIMEOUT),
    [],
  );

  const handleBusinessNameChange = useCallback(
    (option: MultiValue<OptionType> | SingleValue<OptionType>) => {
      if (!option) {
        setValue(ContractorInfoField.ContractorBusinessName, '');
        setValue(ContractorInfoField.IntermediaryId, '');
        setValue(ContractorInfoField.ContractorCity, '');
        setValue(ContractorInfoField.ContractorState, '');
        setValue(ContractorInfoField.ContractorContactName, '');
        setValue(ContractorInfoField.ContractorPhone, '');
        setValue(ContractorInfoField.ContractorContactEmail, '');
        setIsManualEntry(false);
        setContactOptions([]);
        setCurrentBusinessNameInputValue('');

        return;
      }

      const selectedOption = option as OptionType;

      if (selectedOption.__isNew__) {
        setValue(ContractorInfoField.ContractorBusinessName, selectedOption.value);
        setValue(ContractorInfoField.IntermediaryId, '');
        setValue(ContractorInfoField.ContractorCity, '');
        setValue(ContractorInfoField.ContractorState, '');
        setValue(ContractorInfoField.ContractorContactName, '');
        setValue(ContractorInfoField.ContractorPhone, '');
        setValue(ContractorInfoField.ContractorContactEmail, '');
        setIsManualEntry(true);
        setContactOptions([]);
      } else {
        const foundIntermediary = foundSuggestions.find(
          (suggestion) => suggestion.intermediaryName === selectedOption.label,
        );

        setValue(ContractorInfoField.ContractorBusinessName, selectedOption.label);
        setValue(ContractorInfoField.IntermediaryId, foundIntermediary?.id || '');
        setValue(ContractorInfoField.ContractorCity, foundIntermediary?.intermediaryCity || '');
        setValue(ContractorInfoField.ContractorState, foundIntermediary?.intermediaryState || '');
        setValue(ContractorInfoField.ContractorContactName, '');
        setValue(ContractorInfoField.ContractorPhone, '');
        setValue(ContractorInfoField.ContractorContactEmail, '');
        setIsManualEntry(false);
        setContactOptions(foundIntermediary?.intermediaryContacts || []);
      }

      setCurrentBusinessNameInputValue('');
      clearErrors(ContractorInfoField.ContractorBusinessName);

      if (formState.isSubmitted) {
        trigger();
      }
    },
    [foundSuggestions, formState.isSubmitted, setValue, clearErrors, trigger],
  );

  const handleContactNameChange = useCallback(
    (option: MultiValue<OptionType> | SingleValue<OptionType>) => {
      if (!option) {
        setValue(ContractorInfoField.ContractorContactName, '');
        setValue(ContractorInfoField.ContractorPhone, '');
        setValue(ContractorInfoField.ContractorContactEmail, '');
        setCurrentContactNameInputValue('');

        return;
      }

      const selectedOption = option as OptionType;
      const contactName = selectedOption.__isNew__ ? selectedOption.value : selectedOption.label;
      const selectedContact = contactOptions.find((contact) => contact.name === contactName);

      setValue(ContractorInfoField.ContractorContactName, contactName);
      setValue(ContractorInfoField.ContractorPhone, selectedContact?.phone || '');
      setValue(ContractorInfoField.ContractorContactEmail, selectedContact?.email || '');
      setCurrentContactNameInputValue('');
    },
    [contactOptions, setValue],
  );

  const handleOnSubmit = handleSubmit(async () => {
    const isValid = await trigger();

    if (isValid) {
      try {
        dispatch(
          setLoadingState({
            type: `${LoadingType.RequiredInformationUpdating}${DashboardTabName.ContractorInfo}`,
            loading: true,
          }),
        );

        const intermediaryId = watcher[ContractorInfoField.IntermediaryId];
        const cityBeforeSave = watcher[ContractorInfoField.ContractorCity];
        const stateBeforeSave = watcher[ContractorInfoField.ContractorState];
        const updateDto = !watcher[ContractorInfoField.ContractorLocated]
          ? {
              applicationId: applicationId!,
              contractorIsLocated: false,
            }
          : {
              applicationId: applicationId!,
              contractorIsLocated: true,
              businessName: watcher[ContractorInfoField.ContractorBusinessName],
              intermediaryId: intermediaryId || undefined,
              ...(intermediaryId
                ? {}
                : {
                    city: watcher[ContractorInfoField.ContractorCity],
                    state: watcher[ContractorInfoField.ContractorState],
                  }),
              contactName: watcher[ContractorInfoField.ContractorContactName],
              contactPhoneNumber: watcher[ContractorInfoField.ContractorPhone],
              contactEmail: watcher[ContractorInfoField.ContractorContactEmail],
            };

        const contractorInformation = await contractorInformationApi.update(updateDto);

        setFormValuesFromDto(contractorInformation);

        // Backend does not persist city/state for intermediary-linked contractors, so restore locally-displayed values.
        if (intermediaryId && (!contractorInformation.city || !contractorInformation.state)) {
          setValue(ContractorInfoField.ContractorCity, cityBeforeSave);
          setValue(ContractorInfoField.ContractorState, stateBeforeSave);
        }

        dispatch(setLoanPortal({ contractorInformationComplete: true }));

        await dispatch(getApplicationInfoForDashboardThunk(borrowerId!)).unwrap();
      } catch (error) {
        notificationManager.notifyFailure(FailureNotificationType.UnknownError);
      } finally {
        dispatch(
          setLoadingState({
            type: `${LoadingType.RequiredInformationUpdating}${DashboardTabName.ContractorInfo}`,
            loading: false,
          }),
        );
      }
    }
  });

  const isFormDisabled = contractorInformationComplete;
  const hasBusinessName = !!watcher[ContractorInfoField.ContractorBusinessName];

  return loading || isContractorInformationLoading ? (
    <Loader />
  ) : (
    <div className={styles.requiredInformationContainer}>
      <Section
        title="Contractor Information"
        subSectionStates={[
          {
            isExist: true,
            isCompleted: contractorInformationComplete,
          },
        ]}
      >
        <FormProvider
          register={register}
          unregister={unregister}
          trigger={trigger}
          handleSubmit={handleSubmit}
          formState={formState}
          watch={watch}
          setValue={setValue}
          clearErrors={clearErrors}
          {...methods}
        >
          <form onSubmit={handleOnSubmit}>
            <div className={styles.radioButtonsHeader}>
              Have you located a contractor who will complete the work for your Home Improvement?
            </div>
            <div className={styles.radioButtons}>
              <RadioGroup
                value={watcher[ContractorInfoField.ContractorLocated]}
                onChange={(event) => {
                  const currentValue = convertStringToBoolean(event.target.value);

                  setValue(ContractorInfoField.ContractorLocated, currentValue);
                  setIsContractorInfoOpened(!!currentValue);

                  if (formState.isSubmitted) {
                    trigger();
                  }
                }}
              >
                <FormControlLabel
                  classes={{ label: styles.radioButtonLabel }}
                  value={true}
                  disabled={isFormDisabled}
                  control={<Radio classes={{ root: styles.radioButton }} />}
                  label={ContractorLocatedRadioValue.Yes}
                />
                <FormControlLabel
                  classes={{ label: styles.radioButtonLabel }}
                  value={false}
                  disabled={isFormDisabled}
                  control={<Radio classes={{ root: styles.radioButton }} />}
                  label={ContractorLocatedRadioValue.No}
                />
              </RadioGroup>
            </div>
            {!!formState.errors?.[ContractorInfoField.ContractorLocated] && (
              <FormHelperText className={styles.errorMessage}>This Information Is Required</FormHelperText>
            )}
            {isContractorInfoOpened && (
              <div>
                <div className={styles.formRow}>
                  {isFormDisabled ? (
                    <Input
                      label={ContractorInfoLabel.ContractorBusinessName}
                      className={styles.input}
                      name={ContractorInfoField.ContractorBusinessName}
                      value={watcher[ContractorInfoField.ContractorBusinessName]}
                      disabled
                      onChange={() => {}}
                    />
                  ) : (
                    <SelectCreatable
                      name={ContractorInfoField.ContractorBusinessName}
                      loadOptions={loadContractorSuggestions}
                      label={ContractorInfoLabel.ContractorBusinessName}
                      formatCreateLabel={(inputValue: string) => `I cannot find my contractor: "${inputValue}"`}
                      isValidNewOption={() => currentBusinessNameInputValue.length >= MIN_SEARCH_LENGTH}
                      onChange={handleBusinessNameChange}
                      onInputChange={(inputValue: string) => setCurrentBusinessNameInputValue(inputValue)}
                      value={
                        watcher[ContractorInfoField.ContractorBusinessName]
                          ? getInputSelectOptions([watcher[ContractorInfoField.ContractorBusinessName]])[0]
                          : undefined
                      }
                      inputValue={currentBusinessNameInputValue}
                      isClearable={!!watcher[ContractorInfoField.ContractorBusinessName]}
                      inputContainerClassname={styles.input}
                      errorMessage={formState.errors[ContractorInfoField.ContractorBusinessName]?.message}
                    />
                  )}
                </div>
                <div className={styles.formRow}>
                  <Input
                    label={ContractorInfoLabel.ContractorCity}
                    errorMessage={formState.errors[ContractorInfoField.ContractorCity]?.message}
                    className={styles.input}
                    name={ContractorInfoField.ContractorCity}
                    onChange={(event) => {
                      setValue(ContractorInfoField.ContractorCity, event.target.value);
                    }}
                    value={watcher[ContractorInfoField.ContractorCity]}
                    disabled={isFormDisabled || (!isManualEntry && hasBusinessName)}
                  />
                  {!isManualEntry && hasBusinessName ? (
                    <Input
                      label={ContractorInfoLabel.ContractorState}
                      className={styles.input}
                      name={ContractorInfoField.ContractorState}
                      value={watcher[ContractorInfoField.ContractorState]}
                      disabled
                      onChange={() => {}}
                    />
                  ) : (
                    <Select
                      className={styles.input}
                      value={stateOptions.find(
                        (stateOption) => stateOption.value === watcher[ContractorInfoField.ContractorState],
                      )}
                      label={ContractorInfoLabel.ContractorState}
                      name={ContractorInfoField.ContractorState}
                      options={stateOptions}
                      inputContainerClassName={styles.input}
                      isSearchable
                      isClearable
                      errorMessage={formState.errors[ContractorInfoField.ContractorState]?.message}
                      formatOptionLabel={formatOptionLabel}
                      onChange={(option) => {
                        setValue(ContractorInfoField.ContractorState, option ? (option as OptionType).value : '');
                      }}
                      onChangeControl={(value) => {
                        if (!value) {
                          setValue(ContractorInfoField.ContractorState, '');
                        }
                      }}
                      onInputChange={(inputValue) => {
                        if (inputValue) {
                          return inputValue.length <= 2 ? inputValue.replace(/[^A-Za-z]/g, '').slice(0, 2) : '';
                        }
                      }}
                      isDisabled={isFormDisabled}
                    />
                  )}
                </div>
                {hasBusinessName && (
                  <div className={styles.formRow}>
                    {!isFormDisabled && contactOptions.length > 0 ? (
                      <SelectCreatable
                        name={ContractorInfoField.ContractorContactName}
                        label={ContractorInfoLabel.ContractorContactName}
                        formatCreateLabel={(inputValue: string) => `Add: "${inputValue}"`}
                        onChange={handleContactNameChange}
                        onInputChange={(inputValue: string) => setCurrentContactNameInputValue(inputValue)}
                        value={
                          watcher[ContractorInfoField.ContractorContactName]
                            ? getInputSelectOptions([watcher[ContractorInfoField.ContractorContactName]])[0]
                            : undefined
                        }
                        inputValue={currentContactNameInputValue}
                        options={getInputSelectOptions(contactOptions.map((contact) => contact.name))}
                        isClearable={!!watcher[ContractorInfoField.ContractorContactName]}
                        inputContainerClassname={styles.input}
                        errorMessage={formState.errors[ContractorInfoField.ContractorContactName]?.message}
                      />
                    ) : (
                      <Input
                        label={ContractorInfoLabel.ContractorContactName}
                        className={styles.input}
                        name={ContractorInfoField.ContractorContactName}
                        onChange={(event) => {
                          setValue(ContractorInfoField.ContractorContactName, event.target.value);
                        }}
                        value={watcher[ContractorInfoField.ContractorContactName]}
                        disabled={isFormDisabled}
                      />
                    )}
                  </div>
                )}
                <div className={styles.formRow}>
                  <PhoneNumberInput
                    label={ContractorInfoLabel.ContractorContactPhone}
                    name={ContractorInfoField.ContractorPhone}
                    className={styles.input}
                    inputContainerClassname={styles.input}
                    value={watcher[ContractorInfoField.ContractorPhone]}
                    errorMessage={formState.errors[ContractorInfoField.ContractorPhone]?.message}
                    onChange={(phoneValue: string) => {
                      clearErrors(ContractorInfoField.ContractorPhone);
                      setValue(ContractorInfoField.ContractorPhone, phoneValue);
                    }}
                    onBlur={() => {
                      if (watcher[ContractorInfoField.ContractorPhone]) {
                        trigger(ContractorInfoField.ContractorPhone);
                      }
                    }}
                    disabled={isFormDisabled}
                  />
                  <Input
                    label={ContractorInfoLabel.ContractorContactEmail}
                    className={styles.input}
                    name={ContractorInfoField.ContractorContactEmail}
                    onChange={(event) => {
                      setValue(ContractorInfoField.ContractorContactEmail, event.target.value);
                      trigger(ContractorInfoField.ContractorContactEmail);
                    }}
                    value={watcher[ContractorInfoField.ContractorContactEmail]}
                    errorMessage={formState.errors[ContractorInfoField.ContractorContactEmail]?.message}
                    disabled={isFormDisabled}
                  />
                </div>
              </div>
            )}
            {!contractorInformationComplete && <Button onClick={handleOnSubmit}>Save</Button>}
          </form>
        </FormProvider>
      </Section>
    </div>
  );
};

export default ContractorInformationTab;
